import { createStore, emitOnce, select, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import {
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  take,
} from 'rxjs';
import {
  addEntities,
  deleteEntities,
  entitiesPropsFactory,
  getActiveId,
  getAllEntities,
  selectAllEntities,
  selectAllEntitiesApply,
  setActiveId,
  setEntities,
  updateEntities,
  upsertEntities,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { HttpErrorResponse } from '@angular/common/http';
import { getMapValueIfExists } from '@app-shared/functions/helpers.functions';
import { CountryCode } from '@app-shared/enums/country-codes.enum';
import {
  DEFAULT_SORT_BY,
  defaultViewAllSortByFiltersMap,
} from 'src/app/defaults/filter.defaults';

import { ISearchProductData } from './search.model';
import { ProductViews } from '../enums/product-views.enum';
import { SortByFilter, SortByOrder } from '../enums/sort-by-filters.enum';
import { ShortageStatus } from '../enums/shortage-status.enum';
import {
  ITableColumn,
  ITableColumnConfig,
} from '../interfaces/table-column.interface';
import { IProductViewsItemCounts } from '../interfaces/product-views.interface';
import { TimePeriod } from '../components/filters/sortalys-time-period-filter/interfaces/time-period.interface';

export interface ProductsProps {
  searchTerm: string | null;
  suggestionsSearchTerm: string | null;
  selectedProduct: number | null;
  currentPage: number;
  totalPages: number | null;
  totalItems: number | null;
  nextPage: number | null;
  sortBy: SortByFilter | null;
  sortByOrder: SortByOrder | null;
  columnSortBy: SortByFilter | null;
  columnSortByOrder: SortByOrder | null;
  activeSortByFilters: SortByFilter[] | null;
  activeCustomersFilter: number[];
  activeSortalysTimePeriodsFilter: TimePeriod[];
  shortageStatusFilter: ShortageStatus[] | null;
  startDate: Date | null;
  startBackInStockDate: Date | null;
  tableColumnsConfig: ITableColumnConfig[];
  tableColumns: ITableColumn[];
  activeTableColumns: string[];
  productViewsItemCounts: IProductViewsItemCounts | null;
  productView: ProductViews;
  latestUpdateDate: string | null;
  disclaimerText: string;
  hasInitFetch: boolean;
  currentPageIDs: number[];
  suggestionIDs: number[];
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: ProductsProps = {
  searchTerm: null,
  suggestionsSearchTerm: null,
  selectedProduct: null,
  currentPage: 1,
  totalPages: null,
  totalItems: null,
  nextPage: null,
  sortBy: getMapValueIfExists(
    defaultViewAllSortByFiltersMap,
    CountryCode.SWITZERLAND,
    DEFAULT_SORT_BY,
  ),
  sortByOrder: SortByOrder.DESC,
  columnSortBy: null,
  columnSortByOrder: null,
  activeSortByFilters: defaultViewAllSortByFiltersMap.get(
    CountryCode.SWITZERLAND,
  ),
  activeCustomersFilter: [],
  activeSortalysTimePeriodsFilter: [],
  shortageStatusFilter: null,
  startDate: null,
  startBackInStockDate: null,
  tableColumnsConfig: [],
  tableColumns: [],
  activeTableColumns: [],
  productViewsItemCounts: null,
  productView: ProductViews.ALL,
  latestUpdateDate: null,
  disclaimerText: '',
  hasInitFetch: false,
  currentPageIDs: [],
  suggestionIDs: [],
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-products';

@Injectable({ providedIn: 'root' })
export class ProductsRepository {
  private readonly store;
  private readonly selectedProductsEntitiesRef;

  // Main store observables
  public readonly products$: Observable<ISearchProductData[]>;
  public readonly selectedProducts$: Observable<ISearchProductData[]>;
  public readonly selectedProductIds$: Observable<number[]>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;

  public readonly searchTerm$: Observable<string | null>;
  public readonly suggestionsSearchTerm$: Observable<string | null>;
  public readonly selectedProduct$: Observable<number | null>;
  public readonly currentPage$: Observable<number>;
  public readonly totalPages$: Observable<number | null>;
  public readonly totalItems$: Observable<number | null>;
  public readonly nextPage$: Observable<number | null>;
  public readonly sortByFilter$: Observable<SortByFilter | null>;
  public readonly sortByOrder$: Observable<SortByOrder | null>;
  public readonly columnSortByFilter$: Observable<SortByFilter | null>;
  public readonly columnSortByOrder$: Observable<SortByOrder | null>;
  public readonly activeSortByFilters$: Observable<SortByFilter[] | null>;
  public readonly activeCustomersFilter$: Observable<number[]>;
  public readonly activeSortalysTimePeriodsFilter$: Observable<TimePeriod[]>;
  public readonly shortageStatusFilter$: Observable<ShortageStatus[] | null>;
  public readonly startDate$: Observable<Date | null>;
  public readonly productView$: Observable<ProductViews>;
  public readonly tableColumns$: Observable<ITableColumn[]>;
  public readonly activeTableColumns$: Observable<string[]>;
  public readonly productViewsItemCounts$: Observable<{
    all: number;
    back_in_stock: number;
    novelties: number;
    sortalys: number;
    orders: number;
    reported_shortages: number;
    expected_shortages: number;
  }>;
  public readonly latestUpdateDate$: Observable<string | null>;
  public readonly disclaimerText$: Observable<string>;
  public readonly hasInitFetch$: Observable<boolean>;
  public readonly suggestionIDs$: Observable<number[]>;
  public readonly productCount$: Observable<number>;
  public readonly productsSuggestions$: Observable<ISearchProductData[]>;
  public readonly selectedProductsLength$: Observable<boolean>;
  public readonly hasSelectedProducts$: Observable<boolean>;
  public readonly areAllCurrentPageProductsSelected$: Observable<boolean>;
  public readonly currentPageIDs$: Observable<number[]>;

  /** Methods having to do with product selection via checkboxes */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectionActions() {
    return {
      /**
       * Save a selection of products to the store.
       *
       * @param selectedProducts
       */
      setSelectedProducts: (selectedProducts: ISearchProductData[]) => {
        this.store.update(
          setEntities(selectedProducts, {
            ref: this.selectedProductsEntitiesRef,
          }),
        );
      },

      /**
       * Checks if the products is already in the selection array.
       *
       * @param productID
       */
      isProductSelected: (productID: number): boolean => {
        const selectedProducts = this.store.query(
          getAllEntities({ ref: this.selectedProductsEntitiesRef }),
        );

        return selectedProducts.some(p => p.id === productID);
      },

      /**
       * Adds a product to the selection array.
       *
       * @param product
       */
      addProductToSelection: (product: ISearchProductData) => {
        this.store.update(
          addEntities([product], { ref: this.selectedProductsEntitiesRef }),
        );
      },

      /**
       * Removes the product from the selection array.
       *
       * @param productID
       */
      removeProductFromSelection: (productID: number) => {
        this.store.update(
          deleteEntities([productID], {
            ref: this.selectedProductsEntitiesRef,
          }),
        );
      },

      /**
       * Toggles the selection of a product in the selection array.
       *
       * @param product
       */
      toggleSelectionOfProduct: (product: ISearchProductData) => {
        if (this.selectionActions.isProductSelected(product.id)) {
          this.selectionActions.removeProductFromSelection(product.id);
        } else {
          this.selectionActions.addProductToSelection(product);
        }
      },

      /**
       * Returns true if all visible products are selected.
       *
       * Checks the length of selected products and current page products.
       */
      areAllCurrentPageProductsSelected: (): boolean => {
        try {
          const currentPageIDs = this.getStoreProp('currentPageIDs');
          if (!currentPageIDs.length) return false;

          return currentPageIDs.every(id =>
            this.selectionActions.isProductSelected(id),
          );
        } catch {
          return false;
        }
      },

      /**
       * Check if the selection of products has any values.
       *
       * @returns true if at least one product is selected
       */
      selectionHasValues: (): boolean => {
        return !!this.getSelectedProducts().length;
      },

      /**
       * Saves all the products from the current page to the selection array.
       */
      selectAllProductsOnCurrentPage: (): void => {
        let products: ISearchProductData[] = [];
        this.products$.pipe(take(1)).subscribe(p => (products = p));

        const currentPageIDs = this.getStoreProp('currentPageIDs');

        const selectedProducts = currentPageIDs
          .map(id => products.find(product => product.id === id))
          .filter((product): product is ISearchProductData => !!product);

        this.selectionActions.setSelectedProducts(selectedProducts);
      },

      /**
       * Clears the selection of products
       */
      clearSelection: () => {
        this.store.update(
          setEntities([], {
            ref: this.selectedProductsEntitiesRef,
          }),
        );
      },

      /**
       * If all products on the current page are selected then it will unselect them all,
       * otherwise it will select all products on the page
       */
      toggleSelectionOfAllProductsOnPage: () => {
        if (this.selectionActions.areAllCurrentPageProductsSelected()) {
          this.selectionActions.clearSelection();
        } else {
          this.selectionActions.selectAllProductsOnCurrentPage();
        }
      },
    };
  }

  constructor() {
    const { selectedProductsEntitiesRef, withSelectedProductsEntities } =
      entitiesPropsFactory('selectedProducts');

    this.selectedProductsEntitiesRef = selectedProductsEntitiesRef;

    this.store = createStore(
      { name: STORE_NAME },
      withProps<ProductsProps>(initialProps),
      withEntities<ISearchProductData>(),
      withSelectedProductsEntities<ISearchProductData>(),
      withActiveId(),
    );

    this.products$ = combineLatest({
      entities: this.store.pipe(selectAllEntities()),
      currentPageIDs: this.store.pipe(select(state => state.currentPageIDs)),
      sortBy: this.store.pipe(select(state => state.sortBy)),
      sortByOrder: this.store.pipe(select(state => state.sortByOrder)),
    }).pipe(
      debounceTime(0),
      map(({ entities, currentPageIDs }) => {
        // Create an efficient lookup map
        const entitiesMap = new Map(
          entities.map(entity => [entity.id, entity]),
        );

        // Map IDs to entities and filter out any undefined entries
        return currentPageIDs
          .map(id => entitiesMap.get(id))
          .filter(
            (product): product is ISearchProductData => product !== undefined,
          );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedProducts$ = this.store.pipe(
      selectAllEntities({ ref: this.selectedProductsEntitiesRef }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.currentPageIDs$ = this.store.pipe(
      select(state => state.currentPageIDs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // added because of product-selection-actions component
    this.selectedProductsLength$ = this.store.pipe(
      selectAllEntities({ ref: this.selectedProductsEntitiesRef }),
      map(products => products.length > 0), // Convert to boolean based on length
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.hasSelectedProducts$ = this.selectedProducts$.pipe(
      map(products => products.length > 0),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.areAllCurrentPageProductsSelected$ = combineLatest([
      this.currentPageIDs$,
      this.selectedProducts$,
    ]).pipe(
      map(([currentPageIDs, selectedProducts]) => {
        if (!currentPageIDs.length) return false;
        const selectedIds = new Set(selectedProducts.map(p => p.id));

        return currentPageIDs.every(id => selectedIds.has(id));
      }),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedProductIds$ = this.store.pipe(
      selectAllEntitiesApply({
        mapEntity: e => e.id,
        ref: this.selectedProductsEntitiesRef,
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // UI state observables
    this.searchTerm$ = this.store.pipe(
      select(state => state.searchTerm),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.suggestionsSearchTerm$ = this.store.pipe(
      select(state => state.suggestionsSearchTerm),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedProduct$ = this.store.pipe(
      select(state => state.selectedProduct),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.currentPage$ = this.store.pipe(
      select(state => state.currentPage),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.totalPages$ = this.store.pipe(
      select(state => state.totalPages),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.totalItems$ = this.store.pipe(
      select(state => state.totalItems),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.nextPage$ = this.store.pipe(
      select(state => state.nextPage),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.sortByFilter$ = this.store.pipe(
      select(state => state.sortBy),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.sortByOrder$ = this.store.pipe(
      select(state => state.sortByOrder),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.columnSortByFilter$ = this.store.pipe(
      select(state => state.columnSortBy),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.columnSortByOrder$ = this.store.pipe(
      select(state => state.columnSortByOrder),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeSortByFilters$ = this.store.pipe(
      select(state => state.activeSortByFilters),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeCustomersFilter$ = this.store.pipe(
      select(state => state.activeCustomersFilter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeSortalysTimePeriodsFilter$ = this.store.pipe(
      select(state => state.activeSortalysTimePeriodsFilter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.shortageStatusFilter$ = this.store.pipe(
      select(state => state.shortageStatusFilter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.startDate$ = this.store.pipe(
      select(state => state.startDate),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.productView$ = this.store.pipe(
      select(state => state.productView),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tableColumns$ = this.store.pipe(
      select(state => state.tableColumns),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeTableColumns$ = this.store.pipe(
      select(state => state.activeTableColumns),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.productViewsItemCounts$ = this.store.pipe(
      select(state => state.productViewsItemCounts),
      map(counts => {
        if (counts) {
          return {
            all: counts.all ?? 0,
            back_in_stock: counts.back_in_stock ?? 0,
            novelties: counts.novelties ?? 0,
            sortalys: counts.sortalys ?? 0,
            orders: counts.orders ?? 0,
            reported_shortages: counts.reported_shortages ?? 0,
            expected_shortages: counts.expected_shortages ?? 0,
          } satisfies IProductViewsItemCounts;
        }

        return {
          all: 0,
          back_in_stock: 0,
          novelties: 0,
          sortalys: 0,
          orders: 0,
          reported_shortages: 0,
          expected_shortages: 0,
        } satisfies IProductViewsItemCounts;
      }),
    );

    this.latestUpdateDate$ = this.store.pipe(
      select(state => state.latestUpdateDate),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.disclaimerText$ = this.store.pipe(
      select(state => state.disclaimerText),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.hasInitFetch$ = this.store.pipe(
      select(state => state.hasInitFetch),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.suggestionIDs$ = this.store.pipe(
      select(state => state.suggestionIDs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.productCount$ = this.products$.pipe(
      map(value => value.length),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.productsSuggestions$ = combineLatest([
      this.store.pipe(selectAllEntities()),
      this.store.pipe(select(state => state.suggestionIDs)),
    ]).pipe(
      debounceTime(0),
      map(([entities, suggestionIds]) => {
        // Filter and map the entities based on suggestion IDs
        return suggestionIds
          .map(id => entities.find(entity => entity.id === id))
          .filter(
            (entity): entity is ISearchProductData => entity !== undefined,
          );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  resetStore(): void {
    this.store.reset();
  }

  getState(): ProductsProps {
    return this.store.getValue();
  }

  getStoreProp<K extends keyof ProductsProps>(key: K): ProductsProps[K] {
    return this.store.getValue()[key];
  }

  setStoreProp<K extends keyof ProductsProps>(
    key: K,
    value: ProductsProps[K],
  ): void {
    this.store.update(state => ({
      ...state,
      [key]: value,
    }));
  }

  updateStoreProps<K extends keyof ProductsProps>(
    updates: Partial<Pick<ProductsProps, K>>,
  ): void {
    this.store.update(state => ({
      ...state,
      ...updates,
    }));
  }

  // Entity methods
  getActiveId(): number | null {
    return this.store.query(getActiveId);
  }

  upsertProducts(products: ISearchProductData[]): void {
    this.store.update(upsertEntities(products));
  }

  updateProducts(productIDs: number[]): void {
    this.store.update(
      updateEntities(productIDs, {
        last_order_created_on: new Date().toISOString(),
      }),
    );
  }

  getSelectedProducts(): ISearchProductData[] {
    let products: ISearchProductData[] = [];
    this.selectedProducts$.pipe(take(1)).subscribe(p => (products = p));

    return products;
  }

  setLoading(loading: boolean): void {
    this.store.update(state => ({ ...state, loading }));
  }

  setError(error: HttpErrorResponse | null): void {
    this.store.update(state => ({ ...state, error }));
  }

  setActiveProduct(prodId: number): void {
    this.store.update(setActiveId([prodId]));
  }

  setSortByFilter(filter: SortByFilter): void {
    emitOnce(() => {
      this.setStoreProp('currentPage', 1);
      this.setStoreProp('sortBy', filter);
      // Clear column sorting when using regular sort
      this.setStoreProp('columnSortBy', null);
      this.setStoreProp('columnSortByOrder', null);
    });
  }

  setCustomersFilter(
    customersFilter: ProductsProps['activeCustomersFilter'],
  ): void {
    this.updateStoreProps({
      currentPage: 1,
      activeCustomersFilter: customersFilter,
    });
  }

  setStartDate(startDate: ProductsProps['startDate']): void {
    this.store.update(state => ({ ...state, startDate: startDate }));
  }

  setProductView(productView: ProductsProps['productView']): void {
    this.store.update(state => ({ ...state, productView: productView }));
  }

  setSortalysTimePeriodsFilter(
    timePeriodsFilter: ProductsProps['activeSortalysTimePeriodsFilter'],
  ): void {
    this.updateStoreProps({
      currentPage: 1,
      activeSortalysTimePeriodsFilter: timePeriodsFilter,
    });
  }
}
